import { useState } from 'react';
import { ApiFailure, api, type Project } from '../api.ts';
import { Upload } from './Upload.tsx';
import { Recordings } from './Recordings.tsx';
import { Tasks } from './Tasks.tsx';
import { Player } from './Player.tsx';

/**
 * ONE PROJECT, WHOLE. The owner's shape, verbatim: "I open the project and
 * there I can upload audio file(s)… the uploaded files become like cards…
 * when I click the card I can see those versions… from there I can click and
 * see the player." So this page is the project's head (name, type,
 * description — editable), its receiving door (the uploader, `transcribe`
 * projects only), and its recordings as cards; opening a card swaps the page
 * body for the player. Nothing here is a new machine — Upload, Recordings
 * and Player are the very components the deleted tabs used to mount, now
 * living where they always belonged.
 */
export function ProjectPage({
  token,
  projectId,
  project,
  recordingId,
  versionId = '',
  onProjectChanged,
  onSignedOut,
}: {
  token: string;
  projectId: string;
  project: Project | null;
  recordingId: string;
  /** From `…/v/<vid|all>` — which lanes the player opens with. */
  versionId?: string;
  onProjectChanged: () => void;
  onSignedOut: () => void;
}): JSX.Element {
  const [bump, setBump] = useState(0);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function saveHead(): Promise<void> {
    if (busy || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.patchProject(token, projectId, {
        name: name.trim(),
        description: description.trim(),
      });
      setEditing(false);
      onProjectChanged();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not save the project.');
    } finally {
      setBusy(false);
    }
  }

  /* A recording is open: the page IS the player, under one breadcrumb. */
  if (recordingId) {
    return (
      <>
        <section className="card">
          <div className="card-head">
            <a className="btn-link" href={`#/projects/${projectId}`}>
              ← {project?.name ?? 'Project'}
            </a>
          </div>
        </section>
        <Player
          token={token}
          projectId={projectId}
          recordingId={recordingId}
          initialLanes={versionId}
          onRecording={(id) => {
            window.location.hash = `#/projects/${projectId}/r/${id}`;
          }}
          onSignedOut={onSignedOut}
        />
      </>
    );
  }

  return (
    <>
      <section className="card">
        <div className="card-head">
          <a className="btn-link" href="#/projects">
            ← Projects
          </a>
          {!editing ? (
            <>
              <h2>{project?.name ?? '…'}</h2>
              <span className={`pill ${project?.kind === 'record' ? 'pill-processing' : 'pill-review'}`}>
                {project?.kind ?? 'transcribe'}
              </span>
              <span className="topbar-spacer" />
              <button className="btn-link" type="button" onClick={() => setEditing(true)}>
                Edit
              </button>
            </>
          ) : null}
        </div>
        {!editing && project?.description ? (
          <p className="hint" style={{ marginBottom: 0 }}>
            {project.description}
          </p>
        ) : null}
        {editing && (
          <div className="adduser">
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                autoFocus
              />
            </label>
            <label className="field">
              <span className="field-label">Description</span>
              <input
                className="input"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this project is for"
                disabled={busy}
              />
            </label>
            {error && (
              <div className="notice notice-warn" role="status">
                {error}
              </div>
            )}
            <div className="btn-row">
              <button
                className="btn-quiet"
                type="button"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setName(project?.name ?? '');
                  setDescription(project?.description ?? '');
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                type="button"
                style={{ width: 'auto' }}
                disabled={busy || !name.trim()}
                onClick={() => void saveHead()}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </section>

      {project?.kind !== 'record' ? (
        <Upload
          token={token}
          projectId={projectId}
          projectLabel={project?.name ?? ''}
          onUploaded={() => setBump((k) => k + 1)}
          onSignedOut={onSignedOut}
        />
      ) : (
        <Tasks token={token} projectId={projectId} onSignedOut={onSignedOut} />
      )}

      <Recordings
        token={token}
        projectId={projectId}
        refreshKey={bump}
        linkBase={`#/projects/${projectId}/r/`}
        onSignedOut={onSignedOut}
      />
    </>
  );
}
